import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { showErrorToast } from '@amigoapp/doca-react';
import AttendanceEvaluationForm from './AttendanceEvaluationForm';
import { findEvaluationByToken, submitEvaluation } from '../../processes/attendance';
import { findSettingsByEvaluationToken } from '../../processes/company';
import { MAX_OBSERVATION_LENGTH, RATING_HINTS, REASONS_FIELD_BY_RATING_FIELD, LOW_RATING_THRESHOLD } from '../../constants/attendanceEvaluation';
import { useCompanyStore } from '../../stores';
import { DateUtils } from '../../utils';

const isLowRating = rating => rating > 0 && rating <= LOW_RATING_THRESHOLD;

const getRatingHint = rating => (rating ? RATING_HINTS[rating - 1] : 'Toque nas estrelas para avaliar');

const EMPTY_VALUES = {
	clinic_rating: 0,
	clinic_observation: '',
	clinic_reasons: [],
	doctor_rating: 0,
	doctor_observation: '',
	doctor_reasons: []
};

const getInitials = name => (name || '')
	.split(' ')
	.filter(Boolean)
	.slice(0, 2)
	.map(part => part[0].toUpperCase())
	.join('');

function AttendanceEvaluationFormContainer() {
	const params = useParams();
	const token = params.code;

	const { companySettings: { settings }, setCompanySettings } = useCompanyStore();

	const [loading, setLoading] = useState(true);
	const [linkExpired, setLinkExpired] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [attendance, setAttendance] = useState(null);

	const submitToServer = useCallback(async payloadValues => {
		setLoading(true);

		const { error } = await submitEvaluation({ ...payloadValues, token });

		setLoading(false);

		if (error?.code === 403) {
			setLinkExpired(true);

			return false;
		}

		if (error?.message === 'EVALUATION_ALREADY_SUBMITTED') {
			showErrorToast('Você já avaliou este atendimento.');

			return false;
		}

		if (error) {
			showErrorToast('Não foi possível enviar sua avaliação. Tente novamente.');

			return false;
		}

		return true;
	}, [token]);

	const handleSubmitPress = useCallback(async values => {
		const success = await submitToServer(values);

		if (success) {
			setSubmitted(true);
		}
	}, [submitToServer]);

	const formik = useFormik({
		initialValues: EMPTY_VALUES,
		onSubmit: handleSubmitPress
	});

	const { handleChange, handleSubmit, setFieldValue, values } = formik;

	const loadEvaluation = useCallback(async () => {
		if (!token) {
			setLinkExpired(true);
			setLoading(false);

			return;
		}

		setLoading(true);

		const [{ data, error }, companySettingsResult] = await Promise.all([
			findEvaluationByToken({ token }),
			findSettingsByEvaluationToken({ token })
		]);

		setCompanySettings({ settings: companySettingsResult?.data });

		if (error?.message === 'EVALUATION_ALREADY_SUBMITTED') {
			setSubmitted(true);
			setLoading(false);

			return;
		}

		if (error?.code === 403 || error?.message === 'ATTENDANCE_NOT_FOUND') {
			setLinkExpired(true);
			setLoading(false);

			return;
		}

		if (error) {
			showErrorToast('Não foi possível carregar os dados da sua consulta.');
			setLoading(false);

			return;
		}

		if (companySettingsResult?.error) {
			showErrorToast('Não foi possível carregar os dados da empresa.');
		}

		setAttendance(data);
		setLoading(false);
	}, [token, setCompanySettings]);

	useEffect(() => {
		loadEvaluation();
	}, [loadEvaluation]);

	const onSetRating = useCallback((field, rating) => {
		const newRating = rating === values[field] ? 0 : rating;

		setFieldValue(field, newRating);

		if (!isLowRating(newRating)) {
			setFieldValue(REASONS_FIELD_BY_RATING_FIELD[field], []);
		}
	}, [setFieldValue, values]);

	const onToggleReason = useCallback((field, reason) => {
		setFieldValue(field, values[field].includes(reason)
			? values[field].filter(value => value !== reason)
			: [...values[field], reason]);
	}, [setFieldValue, values]);

	const onSetClinicRating = useCallback(rating => onSetRating('clinic_rating', rating), [onSetRating]);
	const onSetDoctorRating = useCallback(rating => onSetRating('doctor_rating', rating), [onSetRating]);
	const onToggleClinicReason = useCallback(reason => onToggleReason('clinic_reasons', reason), [onToggleReason]);
	const onToggleDoctorReason = useCallback(reason => onToggleReason('doctor_reasons', reason), [onToggleReason]);

	const canSubmit = useMemo(
		() => values.clinic_rating > 0
			&& values.doctor_rating > 0
			&& values.clinic_observation.length <= MAX_OBSERVATION_LENGTH
			&& values.doctor_observation.length <= MAX_OBSERVATION_LENGTH,
		[values.clinic_rating, values.doctor_rating, values.clinic_observation, values.doctor_observation]
	);

	const attendanceDateLabel = attendance?.attendance_date
		? DateUtils.formatDate(attendance.attendance_date, 'DD [de] MMMM [às] HH:mm')
		: '';

	const doctorLine = [attendance?.doctor_specialty, attendance?.doctor_crm && `CRM ${attendance.doctor_crm}`]
		.filter(Boolean)
		.join(' · ');

	const clinic = {
		rating: values.clinic_rating,
		hint: getRatingHint(values.clinic_rating),
		reasons: values.clinic_reasons,
		observation: values.clinic_observation,
		onSetRating: onSetClinicRating,
		onToggleReason: onToggleClinicReason
	};

	const doctor = {
		name: attendance?.doctor_name,
		title: attendance?.doctor_title,
		line: doctorLine,
		initials: getInitials(attendance?.doctor_name),
		photoUrl: attendance?.doctor_photo_url,
		rating: values.doctor_rating,
		hint: getRatingHint(values.doctor_rating),
		reasons: values.doctor_reasons,
		observation: values.doctor_observation,
		onSetRating: onSetDoctorRating,
		onToggleReason: onToggleDoctorReason
	};

	return (
		<AttendanceEvaluationForm
			loading={loading}
			linkExpired={linkExpired}
			submitted={submitted}
			variant={settings?.theme?.name}
			companyName={settings?.name}
			companyLogoUrl={settings?.logo?.url}
			unitName={attendance?.place_name}
			patientName={attendance?.patient_name}
			attendanceIntro={
				attendanceDateLabel
					? `Sua consulta de ${attendanceDateLabel} foi concluída. Leva menos de um minuto para avaliar.`
					: 'Sua consulta foi concluída. Leva menos de um minuto para avaliar.'
			}
			clinic={clinic}
			doctor={doctor}
			canSubmit={canSubmit}
			maxObservationLength={MAX_OBSERVATION_LENGTH}
			onChange={handleChange}
			onSubmitClick={handleSubmit}
		/>
	);
}

export default AttendanceEvaluationFormContainer;
